
import { useState, useEffect } from "react";
import { Product, UsageRecord, StockUpdate } from "@/lib/types";
import { useProducts } from "@/hooks/use-products";
import { fetchAnalyticsUsageRecords, fetchAnalyticsStockUpdates } from "@/lib/storage/analytics/remote-data";
import { useToast } from "@/hooks/use-toast";
import { getStartDate } from "@/utils/analytics/dateUtils";

export const useAnalyticsData = () => {
  const [timeRange, setTimeRange] = useState<"7days" | "30days" | "90days">("30days");
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  const [selectedGroupId, setSelectedGroupId] = useState<string>("all-groups");
  const [usageRecords, setUsageRecords] = useState<UsageRecord[]>([]);
  const [stockUpdates, setStockUpdates] = useState<StockUpdate[]>([]);
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(true);
  const { toast } = useToast();
  
  const { products, groups, isLoading } = useProducts();
  
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      setIsAnalyticsLoading(true);
      try {
        const startDate = getStartDate(timeRange);
        
        const [fetchedUsageRecords, fetchedStockUpdates] = await Promise.all([
          fetchAnalyticsUsageRecords(startDate),
          fetchAnalyticsStockUpdates(startDate)
        ]);
        
        setUsageRecords(fetchedUsageRecords);
        setStockUpdates(fetchedStockUpdates);
        
        console.log(`Analytics data loaded: ${fetchedUsageRecords.length} usage records, ${fetchedStockUpdates.length} stock updates`);
      } catch (error) {
        console.error('Error fetching analytics data:', error);
        toast({
          title: "Failed to load analytics data",
          description: "There was an error fetching your data. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsAnalyticsLoading(false);
      }
    };
    
    if (!isLoading && products.length > 0) {
      fetchAnalyticsData();
    }
  }, [timeRange, isLoading, products.length, toast]);

  const handleGroupChange = (groupId: string) => {
    setSelectedGroupId(groupId);
    if (groupId && groupId !== "all-groups") {
      const productsInGroup = products.filter(p => p.groupIds.includes(groupId));
      if (productsInGroup.length > 0 && !productsInGroup.some(p => p.id === selectedProductId)) {
        setSelectedProductId(productsInGroup[0].id);
      }
    }
  };

  return {
    timeRange,
    setTimeRange,
    selectedProductId,
    setSelectedProductId,
    selectedGroupId,
    handleGroupChange,
    usageRecords,
    stockUpdates,
    isAnalyticsLoading,
    products,
    groups,
    isLoading
  };
};
